import { Avatar, Box, SystemIcon, Typography } from '@wishket/design-system';
import { twJoin, twMerge } from 'tailwind-merge';

import CollectionScrapButton from './CollectionScrapButton';

import { lightenHexColor } from '@/features/Collection/CollectionThumbnail/CollectionThumbnail.utils';
import { PATH } from '@/shared/constants';
import { CollectionStatus } from '@/shared/types';
import { Image } from '@/shared/Components';
interface CollectionThumbnailProps {
  collectionId: number;
  count?: number;
  layerColor?: string;
  imgSrc?: string | null;
  profileImgSrc?: string;
  status?: CollectionStatus;
  useScrapButton?: boolean;
}

const LandingCollectionThumbnail = ({
  collectionId,
  imgSrc,
  count = 0,
  layerColor,
  profileImgSrc,
  status = CollectionStatus.PUBLIC,
  useScrapButton,
}: CollectionThumbnailProps) => {
  const collectionThumbnail = {
    default: `${PATH.MEDIA_YOZM_IMAGE}/default_collection_thumbnail.svg`,
    private: `${PATH.MEDIA_YOZM_IMAGE}/private_collection_thumbnail.svg`,
    deleted: `${PATH.MEDIA_YOZM_IMAGE}/deleted_collection_thumbnail.svg`,
  };

  const src = () => {
    switch (status) {
      case CollectionStatus.PUBLIC:
        return imgSrc || collectionThumbnail.default;
      case CollectionStatus.PRIVATE:
        return collectionThumbnail.private;
      case CollectionStatus.DELETED:
        return collectionThumbnail.deleted;
    }
  };

  const containerStyle = twMerge(
    'relative flex items-center justify-center',
    'tablet:h-[121px] h-[94px]'
  );

  const defaultLayerStyle = twMerge(
    `absolute top-0 transform bg-w-bluegray-50 tablet:h-[114px] h-[86px]`,
    'rounded-t-2xl border-w-gray-100 border border-b-0'
  );

  const firstLayerStyle = twMerge(
    defaultLayerStyle,
    'tablet:w-[265px] tablet:translate-y-[7.2px]',
    'w-[210px] translate-y-[10px]'
  );

  const secondLayerStyle = {
    style: { backgroundColor: layerColor },
    className: twMerge(
      defaultLayerStyle,
      'tablet:w-[248px] tablet:translate-y-[3.6px]',
      'w-[198px] translate-y-[5px] border-[1.4px] border-white'
    ),
  };

  const thirdLayerStyle = {
    style: { backgroundColor: lightenHexColor(layerColor) },
    className: twMerge(
      defaultLayerStyle,
      'tablet:w-[236px] w-[186px] border-[1.4px] border-white'
    ),
  };

  return (
    <Box className={containerStyle}>
      <Box
        className={thirdLayerStyle.className}
        style={thirdLayerStyle.style}
      />
      <Box
        style={secondLayerStyle.style}
        className={secondLayerStyle.className}
      />
      <Box className={firstLayerStyle}>
        <Image
          fill
          src={src()}
          alt="collection_thumbnail"
          className="size-full rounded-t-2xl object-cover"
        />
        <Box
          className={twJoin(
            'absolute left-0 top-0 h-[45px] w-full rounded-t-2xl desktop:h-[53px]',
            'bg-gradient-to-b from-black/30 to-transparent'
          )}
        />
      </Box>
      <Box className="absolute left-2 top-5 tablet:left-[13px] tablet:top-[21px]">
        <Box className="flex items-center gap-1 rounded-lg bg-black/50 px-2 py-[3px] backdrop-blur-[2px]">
          <SystemIcon name="small_collection" className="text-w-white" />
          <Typography variant="caption1" className="font-medium text-w-white">
            {count}
          </Typography>
        </Box>
      </Box>
      {useScrapButton && <CollectionScrapButton collectionId={collectionId} />}
      <Box className="absolute -bottom-[15px] left-[13px] flex items-center  justify-center rounded-full border-[2px] border-white bg-white tablet:left-[21px]">
        <Avatar imgUrl={profileImgSrc} size="md" />
      </Box>
    </Box>
  );
};

export default LandingCollectionThumbnail;
